import React, { useState, useRef, useEffect } from 'react';
import './Contact.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import apiService from '../services/api';

// --- Reusable Components ---

function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// --- Main Contact Component ---

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    subject: ''
  });
  const [formStatus, setFormStatus] = useState({ submitted: false, success: false, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [websiteInfo, setWebsiteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);

  // Check for logged in user
  useEffect(() => {
    const checkUser = () => {
      try {
        const user = localStorage.getItem('user');
        if (user) {
          const userData = JSON.parse(user);
          const id = userData.id || userData.user_id || null;
          if (id) {
            setUserId(id);
          }
        }
      } catch (e) {
        console.error('Error checking user:', e);
      }
    };
    checkUser();
  }, []);

  // Fetch website info on component mount
  useEffect(() => {
    const fetchWebsiteInfo = async () => {
      try {
        setLoading(true);
        const result = await apiService.fetchWebinfo();
        if (result.status === 'success' && result.data && result.data.length > 0) {
          setWebsiteInfo(result.data[0]);
        }
      } catch (error) {
        console.error('Error fetching website info:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchWebsiteInfo();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const contactData = {
        user_id: userId || 1,
        subject: formData.subject || 'General Enquiry',
        message: formData.message.trim()
      };

      console.log('📤 Sending contact data:', contactData);

      const result = await apiService.saveData('contact_queries', contactData);
      
      console.log('📝 Form submission result:', result);

      if (result.success) {
        setFormStatus({
          submitted: true,
          success: true,
          message: 'Thank you for your message! We will get back to you shortly.'
        });
        setFormData({ name: '', email: '', phone: '', message: '', subject: '' });
      } else {
        setFormStatus({
          submitted: true,
          success: false,
          message: result.message || 'Failed to send message. Please try again.'
        });
      }
    } catch (error) {
      console.error('❌ Form submission error:', error);
      setFormStatus({
        submitted: true,
        success: false,
        message: 'Network error: ' + (error.message || 'Please check your connection and try again.')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const cleanHtml = (html) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Get contact info from website settings or use defaults
  const getContactInfo = () => {
    // Clean HTML from Summernote
    const cleanAddress = websiteInfo?.address_en ? cleanHtml(websiteInfo.address_en) : 'Mayuri Marg, Begumpet, Hyderabad — 500016';
    const cleanPhone = websiteInfo?.phone_number ? cleanHtml(websiteInfo.phone_number) : '(040) 27767931\n(040) 29562935\n94949 40009';
    const cleanEmail = websiteInfo?.contact_email ? cleanHtml(websiteInfo.contact_email) : 'saibaba_goud@hotmail.com';

    const defaults = [
      {
        icon: 'fa-location-dot',
        title: 'Visit Us',
        details: cleanAddress,
        link: `https://maps.google.com/maps?q=${encodeURIComponent(cleanAddress)}`
      },
      {
        icon: 'fa-envelope',
        title: 'Email Us',
        details: cleanEmail,
        link: `mailto:${cleanEmail}`
      },
      {
        icon: 'fa-phone',
        title: 'Call Us',
        details: cleanPhone,
        link: `tel:${cleanPhone.replace(/[^0-9+]/g, '')}`
      },
      {
        icon: 'fa-clock',
        title: 'Office Hours',
        details: 'Monday - Saturday\n9:00 AM - 5:00 PM',
      }
    ];
    return defaults;
  };

  // Get social media links from website info
  const getSocialLinks = () => {
    if (!websiteInfo) return [];
    
    const socialMap = [
      { key: 'facebook', icon: 'fa-facebook', label: 'Facebook', color: '#1877f2' },
      { key: 'twitter', icon: 'fa-twitter', label: 'Twitter', color: '#000000' },
      { key: 'instagram', icon: 'fa-instagram', label: 'Instagram', color: '#e4405f' },
      { key: 'youtube', icon: 'fa-youtube', label: 'YouTube', color: '#ff0000' },
      { key: 'linkedin', icon: 'fa-linkedin-in', label: 'LinkedIn', color: '#0a66c2' }
    ];

    return socialMap
      .filter(social => websiteInfo[social.key] && websiteInfo[social.key].trim() !== '')
      .map(social => ({
        ...social,
        url: websiteInfo[social.key]
      }));
  };

  const contactInfo = getContactInfo();
  const socialLinks = getSocialLinks();

  // Clean phone for display
  const displayPhone = websiteInfo?.phone_number ? cleanHtml(websiteInfo.phone_number) : '(040) 27767931';
  const displayEmail = websiteInfo?.contact_email ? cleanHtml(websiteInfo.contact_email) : 'saibaba_goud@hotmail.com';
  const displayAddress = websiteInfo?.address_en ? cleanHtml(websiteInfo.address_en) : 'Mayuri Marg, Begumpet, Hyderabad — 500016';

  return (
    <>
      <Header />
      <div className="contact-page devnar">
        <a className="skip-link" href="#main-contact">Skip to main content</a>

        {/* --- Page Hero --- */}
        <section className="page-hero-contact">
          <div className="hero-background">
            <div className="hero-overlay"></div>
            <div className="hero-pattern"></div>
          </div>
          <div className="wrap hero-content-contact">
            <Reveal>
              <span className="hero-badge">Get in Touch</span>
              <h1>Contact Us</h1>
              <p className="hero-subtitle">
                We'd love to hear from you — for admissions, volunteering, CSR partnerships, or general enquiries.
              </p>
            </Reveal>
          </div>
        </section>

        <main id="main-contact" className="wrap page-content-contact">

          {/* --- Contact Info & Form --- */}
          <section className="contact-section">
            <div className="contact-grid">
              {/* Contact Info */}
              <Reveal className="contact-info" as="div" delay={0}>
                <div className="contact-info-header">
                  <h2>Get in Touch</h2>
                  <p className="contact-subtitle">
                    We would love to hear from you — for admissions, volunteering, CSR partnerships or general enquiries.
                  </p>
                </div>
                
                <div className="contact-details">
                  {contactInfo.map((item, index) => (
                    <Reveal key={item.title} className="contact-item" delay={index * 80}>
                      <div className="contact-item-icon">
                        <i className={`fas ${item.icon}`}></i>
                      </div>
                      <div className="contact-item-content">
                        <h3>{item.title}</h3>
                        {item.link ? (
                          <a href={item.link} target="_blank" rel="noopener noreferrer" className="contact-link">
                            {item.details.split('\n').map((line, i) => (
                              <span key={i}>{line}<br /></span>
                            ))}
                          </a>
                        ) : (
                          <p>{item.details.split('\n').map((line, i) => (
                            <span key={i}>{line}<br /></span>
                          ))}</p>
                        )}
                      </div>
                    </Reveal>
                  ))}
                </div>

                {/* Social Links - Only show if there are social links */}
                {socialLinks.length > 0 && (
                  <Reveal className="contact-social" delay={300}>
                    <h3>Connect With Us</h3>
                    <div className="social-links">
                      {socialLinks.map((social) => (
                        <a 
                          key={social.key}
                          href={social.url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="social-link"
                          aria-label={social.label}
                          style={{ '--social-color': social.color }}
                        >
                          <i className={`fab ${social.icon}`}></i>
                        </a>
                      ))}
                    </div>
                  </Reveal>
                )}
              </Reveal>

              {/* Contact Form */}
              <Reveal className="contact-form-wrapper" as="div" delay={100}>
                <div className="form-container">
                  <h2>Leave a Message</h2>
                  <p className="form-subtitle">We'll get back to you as soon as possible.</p>
                  
                  {formStatus.submitted && formStatus.success ? (
                    <div className="form-success">
                      <span className="success-icon">✅</span>
                      <h3>Message Sent!</h3>
                      <p>{formStatus.message}</p>
                      <button 
                        className="btn btn-secondary"
                        onClick={() => setFormStatus({ submitted: false, success: false, message: '' })}
                      >
                        Send Another Message
                      </button>
                    </div>
                  ) : formStatus.submitted && !formStatus.success ? (
                    <div className="form-error">
                      <span className="error-icon">❌</span>
                      <h3>Submission Failed</h3>
                      <p>{formStatus.message}</p>
                      <button 
                        className="btn btn-secondary"
                        onClick={() => {
                          setFormStatus({ submitted: false, success: false, message: '' });
                          setIsSubmitting(false);
                        }}
                      >
                        Try Again
                      </button>
                    </div>
                  ) : (
                    <form className="contact-form" onSubmit={handleSubmit}>
                      <div className="form-group">
                        <label htmlFor="name">Full Name *</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Your full name"
                        />
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="email">Email Address *</label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="your@email.com"
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="phone">Phone Number</label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="Your phone number"
                          />
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="subject">Subject *</label>
                        <select
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select a subject...</option>
                          <option value="Admissions Enquiry">Admissions Enquiry</option>
                          <option value="Volunteering">Volunteering</option>
                          <option value="CSR Partnership">CSR Partnership</option>
                          <option value="Donation Query">Donation Query</option>
                          <option value="General Enquiry">General Enquiry</option>
                          <option value="Feedback">Feedback</option>
                          <option value="Complaint">Complaint</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="message">Message *</label>
                        <textarea
                          id="message"
                          name="message"
                          rows="5"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          placeholder="Write your message here..."
                        />
                      </div>
                      
                      <button 
                        type="submit" 
                        className="btn btn-primary submit-btn"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner"></span> Sending...
                          </>
                        ) : (
                          <>
                            Send Message <span className="btn-arrow">→</span>
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </Reveal>
            </div>
          </section>

          {/* --- Map Section --- */}
          <section className="map-section">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Find Us</span>
              <h2>Visit Our Campus</h2>
              <div className="rule" />
            </Reveal>
            <Reveal className="map-container" delay={100}>
              <iframe 
                src="https://www.google.com/maps?q=Devnar+Foundation+for+the+Blind+Begumpet+Hyderabad&output=embed" 
                loading="lazy" 
                title="Devnar Foundation Location Map"
                className="map-iframe"
                allowFullScreen
              ></iframe>
              <div className="map-overlay">
                <div className="map-details">
                  <i className="fas fa-location-dot map-icon"></i>
                  <div>
                    <strong>Devnar Foundation for the Blind</strong>
                    <p>{displayAddress}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </section>

          {/* --- Quick Connect Section --- */}
          <section className="quick-connect">
            <Reveal className="quick-connect-content" as="div">
              <div className="quick-connect-text">
                <span className="eyebrow">Quick Connect</span>
                <h2>Need Immediate Assistance?</h2>
                <p>Reach out to us directly through phone or email for urgent queries.</p>
              </div>
              <div className="quick-connect-actions">
                <a href={`tel:${websiteInfo?.phone_number?.replace(/[^0-9+]/g, '') || '+914027767931'}`} className="quick-connect-btn phone">
                  <i className="fas fa-phone"></i>
                  <div>
                    <strong>Call Us</strong>
                    <span>{displayPhone}</span>
                  </div>
                </a>
                <a href={`mailto:${websiteInfo?.contact_email || 'saibaba_goud@hotmail.com'}`} className="quick-connect-btn email">
                  <i className="fas fa-envelope"></i>
                  <div>
                    <strong>Email Us</strong>
                    <span>{displayEmail}</span>
                  </div>
                </a>
              </div>
            </Reveal>
          </section>

        </main>
      </div>
      <Footer />
    </>
  );
}